import { darken, lighten, styled } from "@mui/material/styles";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import { createContext, Fragment, type ReactNode, useContext } from "react";

/** A run of a cell's text, and whether it is what the reader searched for. */
export type SearchMatchChunk = { text: string; match: boolean };

/**
 * `text` cut into the runs that match `term` and the runs between them (#6590). Case-insensitive, and
 * `term` is matched as typed: a "." or "(" in it is that character, never a pattern. Every occurrence
 * is found, so "an" marks both in "Banana". No term, or no match, is one unmarked run.
 */
export function searchMatchChunks(text: string, term: string | undefined): SearchMatchChunk[] {
  const query = term?.trim();
  if (!query || !text) return [{ text, match: false }];
  const pattern = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return text
    .split(pattern)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, match: part.toLowerCase() === query.toLowerCase() }));
}

/** What the table's search box holds: the term every cell marks, so each agrees with the box. */
export function tableSearchTerm<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): string {
  const term = table.getState().globalFilter;
  return typeof term === "string" ? term : "";
}

/** The term a scope of cells marks, and the color to mark it in when a table chose one. */
type SearchMatchScopeValue = { term: string; highlight?: string };

const SearchMatchScopeContext = createContext<SearchMatchScopeValue | undefined>(undefined);

/**
 * Lends a search term to every `SearchMatch` inside it that is given no table (#6590). For content a
 * caller builds before the table that draws it exists, such as `TemplateSelector`'s rows, and for a
 * picker whose search box is its own rather than the table's.
 */
export function SearchMatchScope<TData extends MRT_RowData>(
  props: Readonly<{ children: ReactNode } & ({ table: MRT_TableInstance<TData> } | { term: string })>,
) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized scope
  // would lend the term it first read for as long as the table lives (review of #6801).
  "use no memo";

  const value: SearchMatchScopeValue =
    "table" in props
      ? { term: tableSearchTerm(props.table), highlight: props.table.options.mrtTheme.matchHighlightColor }
      : { term: props.term };
  return <SearchMatchScopeContext.Provider value={value}>{props.children}</SearchMatchScopeContext.Provider>;
}

const Mark = styled("mark", { shouldForwardProp: (prop) => prop !== "highlight" })<{ highlight?: string }>(
  ({ highlight, theme }) => ({
    // As MRT marks the cells it draws itself, so a custom cell's match looks like any other. Without
    // a table to ask, the color MRT would choose for this theme.
    backgroundColor:
      highlight ??
      (theme.palette.mode === "dark"
        ? darken(theme.palette.warning.dark, 0.25)
        : lighten(theme.palette.warning.light, 0.5)),
    borderRadius: 2,
    color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black,
    padding: "2px 1px",
  }),
);

/**
 * `text`, with what the reader typed into the table's search marked wherever it occurs (#6590).
 *
 * MRT marks a match only in a cell it draws itself; a column with its own `Cell` — a link, a chip, a
 * two-line name — draws its own text and has none. Such a cell wraps its text in this. Use it only in
 * a column the table's search covers, so a mark never says a field matched when the server did not
 * search it. Given no table, it marks the term of the `SearchMatchScope` around it, and nothing outside
 * one.
 *
 * A `<mark>`, which a screen reader can announce, in MRT's own highlight color for the theme.
 *
 * Not memoized, and neither may be a component that draws it with a `table`: MRT keeps one mutable
 * table instance as the search changes, so a compiled caller caches this element against an
 * unchanged prop and the mark stays on the old term. Such a caller says `"use no memo"` too.
 */
export default function SearchMatch<TData extends MRT_RowData>(
  props: Readonly<{ text: string | null | undefined; table?: MRT_TableInstance<TData> }>,
): ReactNode {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized mark
  // would keep the term it first read (review of #6801).
  "use no memo";

  const scope = useContext(SearchMatchScopeContext);
  const text = props.text ?? "";
  const term = props.table ? tableSearchTerm(props.table) : scope?.term;
  const chunks = searchMatchChunks(text, term);
  if (!chunks.some((chunk) => chunk.match)) return text;
  const highlight = props.table ? props.table.options.mrtTheme.matchHighlightColor : scope?.highlight;
  return chunks.map((chunk, index) =>
    chunk.match ? (
      // The runs never move, so their order is their identity.
      <Mark highlight={highlight} key={index}>
        {chunk.text}
      </Mark>
    ) : (
      <Fragment key={index}>{chunk.text}</Fragment>
    ),
  );
}
